import { useMemo } from 'react';
import { toast } from 'sonner';
import {
  useSettings,
  useUpdateSetting,
  SETTING_KEYS,
  type SystemSetting,
} from '../../api/settings';
import { BedarfUpdateRecipientsCard } from './BedarfUpdateRecipientsCard';

interface KnownSettingMeta {
  key: string;
  title: string;
  description: string;
}

const KNOWN_SETTINGS: KnownSettingMeta[] = [
  {
    key: SETTING_KEYS.BEWERBER_EMAIL_FLOW_ENABLED,
    title: 'Bewerber Email Flow',
    description:
      'Sendet bei jedem Statuswechsel eines Bewerbers auf "Geeignet" automatisch eine interne E-Mail von jobs@juniva.de an mail@juniva.de. Die E-Mail enthält alle Bewerberdaten, hängt alle Dokumente an und bezieht sich (sofern vorhanden) auf die ursprüngliche Bewerbungsmail.',
  },
];

function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  return new Date(value).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function SettingsPage() {
  const { data: settings, isLoading, error } = useSettings();
  const updateMutation = useUpdateSetting();

  const byKey = useMemo(() => {
    const map = new Map<string, SystemSetting>();
    (settings ?? []).forEach((s) => map.set(s.key, s));
    return map;
  }, [settings]);

  const handleToggle = async (key: string, current: boolean) => {
    try {
      await updateMutation.mutateAsync({ key, value: !current });
      toast.success('Einstellung aktualisiert');
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Aktualisierung fehlgeschlagen';
      toast.error(message);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-semibold text-gray-900 mb-6">
          Einstellungen
        </h1>
        <p className="text-gray-500">Lade Einstellungen…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-semibold text-gray-900 mb-6">
          Einstellungen
        </h1>
        <p className="text-red-600">
          Fehler beim Laden der Einstellungen.
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl">
      <h1 className="text-2xl font-semibold text-gray-900 mb-2">
        Einstellungen
      </h1>
      <p className="text-sm text-gray-500 mb-6">
        Systemweite Schalter (nur Administratoren).
      </p>

      <div className="space-y-4">
        {KNOWN_SETTINGS.map((meta) => {
          const setting = byKey.get(meta.key);
          const enabled = setting?.value === true;
          return (
            <div
              key={meta.key}
              className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <h2 className="text-base font-semibold text-gray-900">
                    {meta.title}
                  </h2>
                  <p className="mt-1 text-sm text-gray-600 leading-relaxed">
                    {meta.description}
                  </p>
                </div>

                <label className="inline-flex items-center cursor-pointer shrink-0">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={enabled}
                    onChange={() => handleToggle(meta.key, enabled)}
                    disabled={updateMutation.isPending}
                  />
                  <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:bg-primary-600 transition-colors after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:border-gray-300 after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5 peer-checked:after:border-white" />
                  <span className="ml-3 text-sm font-medium text-gray-700">
                    {enabled ? 'Aktiv' : 'Inaktiv'}
                  </span>
                </label>
              </div>

              {setting && (
                <div className="mt-4 pt-3 border-t border-gray-100 text-xs text-gray-500">
                  Zuletzt geändert{' '}
                  {setting.updatedBy
                    ? `von ${setting.updatedBy.firstName} ${setting.updatedBy.lastName}`
                    : ''}{' '}
                  am {formatDateTime(setting.updatedAt)}
                </div>
              )}
            </div>
          );
        })}

        <BedarfUpdateRecipientsCard
          setting={byKey.get(SETTING_KEYS.BEDARF_UPDATE_RECIPIENTS)}
          formatDateTime={formatDateTime}
        />
      </div>
    </div>
  );
}
